import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useDataEngine } from '@dhis2/app-runtime';

const FiscalYearContext = createContext(null);

const FISCAL_YEAR_PROGRAM = 'NkReudUAW4F';
const FISCAL_YEAR_STAGE = 'htIWgNQWEMm';
const DATA_ELEMENTS = {
  startPattern: 'U55MqwBcRAe',
  endPattern: 'kgdk4NXu4f5',
};

const formatDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const resolveFiscalYear = (startPattern, endPattern) => {
  const startDate = new Date(startPattern);
  const endDate = new Date(endPattern);
  const now = new Date();
  const currentYear = now.getFullYear();
  const julyFirstCurrentYear = new Date(currentYear, 6, 1);
  const useNextYear = now >= julyFirstCurrentYear;
  const fiscalStartYear = useNextYear ? currentYear : currentYear - 1;
  const fiscalEndYear = useNextYear ? currentYear + 1 : currentYear;
  const fiscalStart = new Date(fiscalStartYear, startDate.getMonth(), startDate.getDate());
  const fiscalEnd = new Date(fiscalEndYear, endDate.getMonth(), endDate.getDate());
  return {
    start: formatDate(fiscalStart),
    end: formatDate(fiscalEnd),
  };
};

export const FiscalYearProvider = ({ children }) => {
  const engine = useDataEngine();
  const [fiscalYear, setFiscalYear] = useState({ start: '', end: '' });
  const [loading, setLoading] = useState(true);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    const fetchFiscalYear = async () => {
      try {
        const response = await engine.query({
          events: {
            resource: 'events',
            params: {
              program: FISCAL_YEAR_PROGRAM,
              programStage: FISCAL_YEAR_STAGE,
              ouMode: 'ACCESSIBLE',
              pageSize: 1,
              order: 'eventDate:desc',
              fields: 'event,dataValues[dataElement,value]',
            },
          },
        });
        const event = response.events?.events?.[0];
        if (event) {
          const dataValues = event.dataValues || [];
          const startPattern = dataValues.find(dv => dv.dataElement === DATA_ELEMENTS.startPattern)?.value;
          const endPattern = dataValues.find(dv => dv.dataElement === DATA_ELEMENTS.endPattern)?.value;
          if (startPattern && endPattern) {
            const resolved = resolveFiscalYear(startPattern, endPattern);
            if (isMounted.current) setFiscalYear(resolved);
          } else {
            // fallback
            const now = new Date();
            const currentYear = now.getFullYear();
            const defaultStart = now >= new Date(currentYear, 6, 1) 
              ? `${currentYear}-07-01` 
              : `${currentYear - 1}-07-01`;
            const defaultEnd = now >= new Date(currentYear, 6, 1)
              ? `${currentYear + 1}-06-30`
              : `${currentYear}-06-30`;
            if (isMounted.current) setFiscalYear({ start: defaultStart, end: defaultEnd });
          }
        } else {
          // no event – default
          const now = new Date();
          const currentYear = now.getFullYear();
          const defaultStart = now >= new Date(currentYear, 6, 1) 
            ? `${currentYear}-07-01` 
            : `${currentYear - 1}-07-01`;
          const defaultEnd = now >= new Date(currentYear, 6, 1)
            ? `${currentYear + 1}-06-30`
            : `${currentYear}-06-30`;
          if (isMounted.current) setFiscalYear({ start: defaultStart, end: defaultEnd });
        }
      } catch (error) {
        console.error('Failed to fetch fiscal year:', error);
        // fallback
        const now = new Date();
        const currentYear = now.getFullYear();
        const defaultStart = now >= new Date(currentYear, 6, 1) 
          ? `${currentYear}-07-01` 
          : `${currentYear - 1}-07-01`;
        const defaultEnd = now >= new Date(currentYear, 6, 1)
          ? `${currentYear + 1}-06-30`
          : `${currentYear}-06-30`;
        if (isMounted.current) setFiscalYear({ start: defaultStart, end: defaultEnd });
      } finally {
        if (isMounted.current) setLoading(false);
      }
    };

    fetchFiscalYear();

    return () => {
      isMounted.current = false;
    };
  }, [engine]);

  return (
    <FiscalYearContext.Provider value={{ fiscalYear, loading }}>
      {children}
    </FiscalYearContext.Provider>
  );
};

export const useFiscalYear = () => useContext(FiscalYearContext);